// The pure field helpers every BI call site shares — no React, no logger, no platform SDK.
//
// They live apart from `use-generic-bi` because that module pulls in the generic-events library and the
// web-bi-logger, and a suite that `jest.mock`s it to capture clicks replaces these along with the
// loggers — so `useItemSeenBi`, which only ever wanted `toKebab`, got `undefined` and every test that
// rendered a card carrying an impression died with "toKebab is not a function". Same reason
// `lib/brief-date-key.ts` and friends exist: the pure part of a heavy module belongs in its own file
// (see the package's test rule). `use-generic-bi` re-exports all three, so no call site changes.

// Entity type FQDNs (from the ISA protos). `agent` and `notification` have no
// proto entity, so callers pass the documented lowercase-name fallback.
//
// These literals are a CROSS-SURFACE CONTRACT: the same strings are declared in the
// native apps (`SymphonyBI.swift` / `SymphonyBi.kt`) so a funnel joins web to iOS to
// Android on `entity_type`. Renaming one here silently breaks the join — change all
// three together, and `test/bi-entity-parity.test.ts` fails if they drift.
export const ENTITY_TYPE = {
  task: 'wix.individual_scheduling_assistant.v1.task',
  conversation: 'wix.individual_scheduling_assistant.v1.conversation',
  pulse: 'wix.individual_scheduling_assistant.v1.pulse_identity',
  // The workspace FQDN comes from the generated WorkspaceClient — it is a `wix.symphony.v1`
  // entity, NOT an `individual_scheduling_assistant` one, which is the easy mistake here.
  workspace: 'wix.symphony.v1.workspace',
  // No proto entity exists for these four, so they use the documented lowercase-name
  // fallback, exactly as the native apps do.
  agent: 'agent',
  maestro: 'maestro',
  tab: 'tab',
  personality: 'personality',
} as const;

// Generic events want kebab-case, lowercase component names. The existing src:172
// names are snake_case (e.g. `create_new_task`) or dynamic (`connect_${toolkit}`),
// so normalize at the boundary rather than touching every call site.
export const toKebab = (value?: string): string =>
  (value ?? '')
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[\s_/]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();

// `additionalFields` must be a flat Record<string, string>; drop empty/undefined
// so we don't emit blank keys. Exported for `use-item-seen-bi`, which normalizes the same way.
export const toAdditionalFields = (
  fields: Record<string, string | undefined>,
): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(fields)) {
    if (value !== undefined && value !== null && value !== '') {
      out[key] = value;
    }
  }
  return out;
};
